/**
 * <nosto-react> — bridge between the Liquid skeleton and the Preact app.
 *
 * Watches `#nosto-content` (where `Category`, `Serp`, etc. mount via portal)
 * for child insertions. Once the first child appears, marks the wrapper
 * `.nosto-loaded` so CSS hides the skeleton and reveals the React tree.
 *
 * If no child shows up within `TIMEOUT_MS`, marks the wrapper `.nosto-fallback`
 * so CSS reveals the native Liquid fallback (the original Dawn collection
 * grid). This is the safety net the user asked for: if Nosto fails or is
 * slow, the customer never sees an empty page.
 *
 * Dispatched events:
 *   - `nosto:ready`  → bubbles when content mounts
 *   - `nosto:fallback` → bubbles when timeout fires
 */

const TIMEOUT_MS = 5000;

class NostoReact extends HTMLElement {
	connectedCallback() {
		this._timeoutId = setTimeout(() => this._fallback(), TIMEOUT_MS);

		const target = this.querySelector('#nosto-content');
		if (!target) {
			this._fallback();
			return;
		}

		// Already mounted by the time we connect (rare but happens with cache hits)
		if (target.children.length > 0) {
			this._ready();
			return;
		}

		this._observer = new MutationObserver(() => {
			if (target.children.length > 0) this._ready();
		});
		this._observer.observe(target, { childList: true });
	}

	disconnectedCallback() {
		this._cleanup();
	}

	_ready() {
		// Cleanup timers/observers and flip state. Crucially, if the
		// fallback class was already applied (Nosto took longer than the
		// timeout, e.g. search page or slow network), we now swap it for
		// `nosto-loaded` so the React tree replaces the native fallback.
		this._cleanup();
		this.classList.remove('nosto-fallback');
		this.classList.add('nosto-loaded');
		this.dispatchEvent(new CustomEvent('nosto:ready', { bubbles: true }));
	}

	_fallback() {
		// Only the timer is cleared; the observer keeps running so a late
		// Nosto mount can still promote us to `loaded`.
		if (this._timeoutId) {
			clearTimeout(this._timeoutId);
			this._timeoutId = null;
		}
		// Avoid flipping if `nosto-loaded` already won the race.
		if (this.classList.contains('nosto-loaded')) return;
		this.classList.add('nosto-fallback');
		this.dispatchEvent(new CustomEvent('nosto:fallback', { bubbles: true }));
	}

	_cleanup() {
		if (this._observer) {
			this._observer.disconnect();
			this._observer = null;
		}
		if (this._timeoutId) {
			clearTimeout(this._timeoutId);
			this._timeoutId = null;
		}
	}
}

if (!customElements.get('nosto-react')) {
	customElements.define('nosto-react', NostoReact);
}
